import { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Radio,
  ColorPicker, App as AntdApp, Spin, Row, Col, Grid,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import dayjs from 'dayjs';
import { projectService, type ProjectDetail, type WorkflowMode } from '@/services/projectService';
import { userService } from '@/services/userService';
import { orgService } from '@/services/rolesService';
import { useAuthStore } from '@/store/authStore';

interface FormValues {
  name: string;
  code?: string;
  description?: string;
  color?: string;
  // V1.0.34: Multi-dept — phần tử đầu = primary, các phần tử sau = additional
  departmentIds?: string[];
  workflowMode: WorkflowMode;
  // V1.0.56: Tách dateRange → 2 field riêng cho mobile-friendly
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  initialMemberIds?: string[];
}

export function CreateProjectModal({ open, onClose, onCreated, defaultDepartmentId }: {
  open: boolean;
  onClose: () => void;
  onCreated: (project: ProjectDetail) => void;
  /** Department ID pre-fill khi mở modal (vd từ sider đang chọn) */
  defaultDepartmentId?: string;
}) {
  const [form] = Form.useForm<FormValues>();
  const { message } = AntdApp.useApp();
  const [submitting, setSubmitting] = useState(false);
  const currentUser = useAuthStore(s => s.user);

  // V1.0.56: Detect mobile để tối ưu UX chọn ngày
  // - Trên mobile: dùng inputReadOnly để ngăn iOS/Android keyboard popup,
  //   chỉ hiện calendar picker (dễ chọn hơn)
  // - 2 DatePicker xếp dọc trên mobile (xs=24), ngang trên desktop (sm=12)
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm; // < 576px = mobile

  // V1.0.34: Khi modal mở: pre-fill departmentIds (multi-select)
  useEffect(() => {
    if (!open) return;
    const dept = defaultDepartmentId ?? currentUser?.department?.id;
    if (dept) {
      form.setFieldValue('departmentIds', [dept]);
    }
  }, [open, defaultDepartmentId, currentUser, form]);

  // Lấy users để thêm member ban đầu
  const { data: usersData } = useQuery({
    queryKey: ['users-simple-list'],
    queryFn: () => userService.list({ pageSize: 1000 }),
    enabled: open,
  });
  const userOptions = (usersData?.items ?? []).map(u => ({
    label: `${u.fullName} (${u.username})`,
    value: u.id,
  }));

  // Lấy departments
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree'],
    queryFn: () => orgService.getTree(),
    enabled: open,
  });
  const flattenOrg = (nodes: any[], prefix = ''): Array<{label: string; value: string}> => {
    const out: Array<{label: string; value: string}> = [];
    for (const n of nodes) {
      // V1.0.47: Bỏ qua khoa inactive
      if (n.isActive === false) {
        if (n.children?.length) out.push(...flattenOrg(n.children, prefix));
        continue;
      }
      out.push({ label: `${prefix}${n.name}`, value: n.id });
      if (n.children?.length) out.push(...flattenOrg(n.children, prefix + '— '));
    }
    return out;
  };
  const deptOptions = flattenOrg(orgTree);

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      setSubmitting(true);

      // V1.0.34: Multi-dept — phần tử đầu của departmentIds = primary, các phần tử sau = additional
      const deptIds: string[] = Array.isArray(v.departmentIds) ? v.departmentIds : (v.departmentIds ? [v.departmentIds] : []);
      const primaryDept = deptIds[0];
      const additionalDepts = deptIds.slice(1);

      const created = await projectService.create({
        name: v.name.trim(),
        code: v.code?.trim() || undefined,
        description: v.description?.trim() || undefined,
        color: typeof v.color === 'string' ? v.color :
               (v.color as any)?.toHexString?.() ?? '#1976D2',
        departmentId: primaryDept || undefined,
        additionalDepartmentIds: additionalDepts.length > 0 ? additionalDepts : undefined,
        workflowMode: v.workflowMode,
        startDate: v.startDate?.format('YYYY-MM-DD'),
        endDate: v.endDate?.format('YYYY-MM-DD'),
        initialMemberIds: v.initialMemberIds && v.initialMemberIds.length > 0
          ? v.initialMemberIds : undefined,
      });

      form.resetFields();
      onCreated(created);
    } catch (e: any) {
      if (e.errorFields) return;
      message.error(e.response?.data?.message ?? 'Tạo dự án thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleSubmit}
      okText="Tạo dự án"
      cancelText="Hủy"
      confirmLoading={submitting}
      title="Tạo dự án mới"
      width={isMobile ? '95vw' : 620}
      style={isMobile ? { top: 8 } : undefined}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{
        workflowMode: 'PARALLEL',
        color: '#1976D2',
      }}>
        <Form.Item
          name="name" label="Tên dự án"
          rules={[{ required: true, message: 'Bắt buộc nhập tên' }]}
        >
          <Input placeholder="VD: Tuyển dụng KS Q2/2026" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="code" label="Mã dự án (tùy chọn)" extra="Để trống sẽ tự sinh">
            <Input placeholder="VD: TD-2026-Q2" />
          </Form.Item>
          <Form.Item name="color" label="Màu chủ đạo">
            <ColorPicker showText format="hex" />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="Mục tiêu, phạm vi của dự án..." />
        </Form.Item>

        <Form.Item
          name="departmentIds"
          label="Khoa được xem dự án"
          extra="Khoa đầu tiên = khoa chủ quản. Có thể chọn thêm khoa phụ — toàn bộ nhân viên các khoa này đều thấy dự án."
        >
          <Select
            mode="multiple"
            placeholder="Chọn 1 hoặc nhiều khoa/phòng"
            options={deptOptions}
            allowClear
            showSearch
            filterOption={(input, opt) =>
              (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item
          name="workflowMode" label="Quy trình thực hiện"
          rules={[{ required: true }]}
          extra="Có thể đổi sau khi tạo"
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: <span><GitBranch size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Song song (PARALLEL)</span>, value: 'PARALLEL' },
              { label: <span><GitBranch size={12} style={{ verticalAlign: -2, marginRight: 4, transform: 'rotate(90deg)' }} />Tuần tự (SEQUENTIAL)</span>, value: 'SEQUENTIAL' },
            ]}
          />
        </Form.Item>

        <div style={{ fontSize: 11, color: '#7d8a98', marginTop: -10, marginBottom: 12, paddingLeft: 4 }}>
          • <b>Song song:</b> các task chạy độc lập, ai làm trước cũng được<br />
          • <b>Tuần tự:</b> các task có dependency, task A xong mới mở task B
        </div>

        {/* V1.0.56: Tách RangePicker → 2 DatePicker riêng cho mobile-friendly
            - RangePicker mở popup 2 tháng cạnh nhau, không vừa màn hình mobile
            - 2 DatePicker riêng dễ chọn từng ngày, gọn hơn nhiều */}
        <Form.Item label="Thời gian dự án" style={{ marginBottom: 8 }}>
          <Row gutter={isMobile ? [0, 8] : 12}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="startDate"
                noStyle
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const end = getFieldValue('endDate');
                      if (value && end && value.isAfter(end)) {
                        return Promise.reject(new Error('Ngày bắt đầu phải trước ngày kết thúc'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="Ngày bắt đầu"
                  style={{ width: '100%' }}
                  inputReadOnly={isMobile}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="endDate"
                noStyle
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue('startDate');
                      if (value && start && value.isBefore(start)) {
                        return Promise.reject(new Error('Ngày kết thúc phải sau ngày bắt đầu'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="Ngày kết thúc"
                  style={{ width: '100%' }}
                  inputReadOnly={isMobile}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item name="initialMemberIds" label="Thêm thành viên (tùy chọn)"
          extra="Người tạo tự động là Owner. Người khác mặc định Member.">
          <Select
            mode="multiple"
            placeholder="Chọn thành viên ban đầu..."
            options={userOptions}
            showSearch
            filterOption={(input, opt) =>
              (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={!usersData ? <Spin size="small" /> : 'Không tìm thấy'}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
